import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { reportApi } from '../../lib/api'
import Icon from '../../components/Icon'
import Badge from '../../components/Badge'
import Spinner from '../../components/Spinner'
import { formatDate } from '../../lib/utils'

const FILTERS = ['All', 'Pending', 'Acknowledged', 'Resolved']

export default function MyReportsPage() {
  const [filter, setFilter] = useState('All')

  const { data: reports = [], isLoading, error } = useQuery({
    queryKey: ['reports-list'],
    queryFn: () => reportApi.list().then((res) => res.data),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  const visible =
    filter === 'All'
      ? reports
      : reports.filter((r) => String(r.status).toUpperCase() === filter.toUpperCase())

  if (isLoading && !error) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading your reports..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 md:px-10 py-6">
        <div className="max-w-md mx-auto bg-status-urgent/10 border border-status-urgent rounded-xl p-8 text-center">
          <p className="text-body-md text-status-urgent mb-4">
            Could not load your reports. Please check your connection and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 bg-accent text-surface-dark font-semibold text-label-md px-6 py-3 rounded-xl hover:bg-accent-light transition"
          >
            <Icon name="refresh" className="text-xl" />
            Refresh
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 md:px-10 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-headline-lg-mobile md:text-headline-lg">My Reports</h1>
          <p className="text-body-md text-text-secondary">
            Track the status of your submitted civic issues.
          </p>
        </div>
        <Link
          to="/report/new"
          className="hidden sm:inline-flex items-center gap-2 bg-accent text-surface-dark font-semibold text-label-md px-4 py-2.5 rounded-xl hover:bg-accent-light active:scale-95 transition shrink-0"
        >
          <Icon name="add" className="text-xl" />
          Report Issue
        </Link>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-label-md whitespace-nowrap min-h-0 transition ${
              filter === f
                ? 'bg-accent text-surface-dark'
                : 'bg-surface-high text-text-secondary hover:text-text-primary'
            }`}
          >
            {f === 'All' ? 'All Reports' : f}
          </button>
        ))}
      </div>

      {error && (
        <div className="p-4 bg-status-urgent/10 border border-status-urgent rounded-xl">
          <p className="text-status-urgent">Failed to load reports</p>
        </div>
      )}

      {/* List */}
      {visible.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl text-center py-12">
          <p className="text-body-lg text-text-secondary mb-4">
            {filter === 'All'
              ? "You haven't submitted any reports yet."
              : `No ${filter.toLowerCase()} reports.`}
          </p>
          <Link
            to="/report/new"
            className="inline-flex items-center gap-2 bg-accent text-surface-dark font-semibold text-label-md px-6 py-3 rounded-xl hover:bg-accent-light active:scale-95 transition"
          >
            <Icon name="add_circle" className="text-xl" />
            Report an Issue
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((report) => (
            <Link
              key={report.id}
              to={`/reports/${report.id}`}
              className="flex gap-4 bg-surface border border-border rounded-xl p-3 hover:border-accent/50 transition-colors"
            >
              <div className="w-20 h-20 rounded-lg bg-surface-high shrink-0 overflow-hidden flex items-center justify-center">
                {report.photo_thumb_path ? (
                  <img
                    src={report.photo_thumb_path}
                    alt="Report"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="image" className="text-text-muted text-2xl" />
                )}
              </div>

              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-label-md text-text-primary leading-snug">{report.category}</h3>
                  <Badge status={report.status} className="shrink-0" />
                </div>
                {report.location_text && (
                  <p className="text-body-md text-text-secondary line-clamp-1">
                    {report.location_text}
                  </p>
                )}
                <div className="flex items-center gap-1.5 text-text-muted text-label-sm pt-0.5">
                  <Icon name="calendar_today" className="text-sm" />
                  Submitted {formatDate(report.created_at)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
